import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

/**
 * Calendar days are stored as local 'YYYY-MM-DD' strings, so string comparison == date order
 * and grouping never needs timezone math.
 */
export type ISODate = string;

/** Week starts on Monday. */
const WEEK = { weekStartsOn: 1 } as const;

export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function toISODate(date: Date): ISODate {
  return format(date, 'yyyy-MM-dd');
}

export function parseISODate(iso: ISODate): Date {
  return parse(iso, 'yyyy-MM-dd', new Date(2000, 0, 1));
}

export function isValidISODate(iso: string): boolean {
  return (
    ISO_DATE_RE.test(iso) && isValid(parseISODate(iso)) && toISODate(parseISODate(iso)) === iso
  );
}

export function toTime(date: Date): string {
  return format(date, 'HH:mm');
}

export function addDaysISO(iso: ISODate, days: number): ISODate {
  return toISODate(addDays(parseISODate(iso), days));
}

export type DateRange = { from: ISODate; to: ISODate };

export type RangePreset =
  'today' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'last3Months' | 'last6Months' | 'thisYear';

export const RANGE_PRESETS: { key: RangePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'thisWeek', label: 'This week' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'last3Months', label: '3 months' },
  { key: 'last6Months', label: '6 months' },
  { key: 'thisYear', label: 'This year' },
];

/** Inclusive range for a preset. Ranges that include today end on today, not in the future. */
export function rangeForPreset(preset: RangePreset, now: Date): DateRange {
  const today = toISODate(now);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'thisWeek':
      return { from: toISODate(startOfWeek(now, WEEK)), to: today };
    case 'thisMonth':
      return { from: toISODate(startOfMonth(now)), to: today };
    case 'lastMonth': {
      const last = addMonths(now, -1);
      return { from: toISODate(startOfMonth(last)), to: toISODate(endOfMonth(last)) };
    }
    case 'last3Months':
      return { from: toISODate(startOfMonth(addMonths(now, -2))), to: today };
    case 'last6Months':
      return { from: toISODate(startOfMonth(addMonths(now, -5))), to: today };
    case 'thisYear':
      return { from: toISODate(startOfYear(now)), to: today };
  }
}

/** 'YYYY-MM' of a date. */
export function monthKey(date: Date): string {
  return format(date, 'yyyy-MM');
}

export function monthStart(key: string): Date {
  return parse(key, 'yyyy-MM', new Date(2000, 0, 1));
}

export function shiftMonth(key: string, delta: number): string {
  return monthKey(addMonths(monthStart(key), delta));
}

/** Full calendar range of a month. */
export function monthRange(key: string): DateRange {
  const start = monthStart(key);
  return { from: toISODate(start), to: toISODate(endOfMonth(start)) };
}

export function daysInRange({ from, to }: DateRange): number {
  return differenceInCalendarDays(parseISODate(to), parseISODate(from)) + 1;
}

export function eachDayISO({ from, to }: DateRange): ISODate[] {
  const days: ISODate[] = [];
  for (let d = parseISODate(from); toISODate(d) <= to; d = addDays(d, 1)) days.push(toISODate(d));
  return days;
}

/** Monday of the week containing `iso`. */
export function weekStartISO(iso: ISODate): ISODate {
  return toISODate(startOfWeek(parseISODate(iso), WEEK));
}

export function eachWeekStartISO({ from, to }: DateRange): ISODate[] {
  const weeks: ISODate[] = [];
  for (let d = startOfWeek(parseISODate(from), WEEK); toISODate(d) <= to; d = addDays(d, 7)) {
    weeks.push(toISODate(d));
  }
  return weeks;
}

export function eachMonthKey({ from, to }: DateRange): string[] {
  const months: string[] = [];
  const last = to.slice(0, 7);
  for (let k = from.slice(0, 7); k <= last; k = shiftMonth(k, 1)) months.push(k);
  return months;
}

export function endOfWeekISO(iso: ISODate): ISODate {
  return toISODate(endOfWeek(parseISODate(iso), WEEK));
}

/** "Today", "Yesterday", "Wed, 30 Sep" or "Wed, 30 Sep 2025". */
export function formatDayHeading(iso: ISODate, now: Date): string {
  const today = toISODate(now);
  if (iso === today) return 'Today';
  if (iso === addDaysISO(today, -1)) return 'Yesterday';
  const date = parseISODate(iso);
  return format(date, iso.slice(0, 4) === today.slice(0, 4) ? 'EEE, d MMM' : 'EEE, d MMM yyyy');
}

/** "02 Oct 2026". */
export function formatDate(iso: ISODate): string {
  return format(parseISODate(iso), 'dd MMM yyyy');
}

/** "2 Oct" — short, for chart tooltips and chips. */
export function formatShortDate(iso: ISODate): string {
  return format(parseISODate(iso), 'd MMM');
}

/** "October 2026". */
export function formatMonth(key: string): string {
  return format(monthStart(key), 'MMMM yyyy');
}

/** "Oct". */
export function formatMonthShort(key: string): string {
  return format(monthStart(key), 'MMM');
}

/** "12:43 PM" from "12:43". */
export function formatTime(time: string): string {
  return format(parse(time, 'HH:mm', new Date(2000, 0, 1)), 'h:mm a');
}

/** Human label for an arbitrary range: "1 – 31 Oct 2026". */
export function formatRange({ from, to }: DateRange): string {
  if (from === to) return formatDate(from);
  const a = parseISODate(from);
  const b = parseISODate(to);
  if (from.slice(0, 7) === to.slice(0, 7)) return `${format(a, 'd')} – ${format(b, 'd MMM yyyy')}`;
  if (from.slice(0, 4) === to.slice(0, 4))
    return `${format(a, 'd MMM')} – ${format(b, 'd MMM yyyy')}`;
  return `${format(a, 'd MMM yyyy')} – ${format(b, 'd MMM yyyy')}`;
}
